// ---------------------------------------------------------------------------
// Icon, ported from the design system (R7)
//
// The system's 58 icons on a 20px grid, 1.5px stroke, drawn in currentColor:
// the 34 of R1 and the 24 R6f drew. The paths are the ones in
// docs/design/system/components/bundle.js, copied as they are, in its order;
// tests/design-ports.test.ts renders every one on both sides, so a change in
// the system that is not copied here fails.
// ---------------------------------------------------------------------------

import { html, nothing, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';

import { cx } from './helpers.js';

export const ICONS = {
  agent: '<circle cx="10" cy="10" r="2.5"/><path d="M10 3a7 7 0 0 1 7 7M10 17a7 7 0 0 1-7-7"/>',
  launch: '<path d="M17 3 3 8.8l5.8 2.4L11.2 17z"/><path d="m8.8 11.2 4-4"/>',
  play: '<path d="M6.5 4.8v10.4l8.3-5.2z"/>',
  pause: '<path d="M7.5 5v10M12.5 5v10"/>',
  stop: '<rect x="5.5" y="5.5" width="9" height="9" rx="1"/>',
  check: '<path d="m4.5 10.5 3.5 3.5 7.5-8"/>',
  close: '<path d="m5 5 10 10M15 5 5 15"/>',
  alert: '<path d="M10 3.5 17.2 16H2.8z"/><path d="M10 8.5V12M10 14.2v.1"/>',
  info: '<circle cx="10" cy="10" r="7"/><path d="M10 9v4.5M10 6.6v.1"/>',
  clock: '<circle cx="10" cy="10" r="7"/><path d="M10 6v4l2.5 2"/>',
  calendar: '<rect x="3.5" y="4.5" width="13" height="12" rx="1.5"/><path d="M3.5 8.5h13M7 3v3M13 3v3"/>',
  mail: '<rect x="3" y="5" width="14" height="10" rx="1.5"/><path d="m3.5 6 6.5 5 6.5-5"/>',
  network:
    '<circle cx="5.5" cy="10" r="2"/><circle cx="14.5" cy="5.5" r="2"/><circle cx="14.5" cy="14.5" r="2"/><path d="m7.3 9.1 5.4-2.7M7.3 10.9l5.4 2.7"/>',
  phone:
    '<path d="M6.2 3.5h2l1 3.4-1.6 1.1a8.5 8.5 0 0 0 4.4 4.4l1.1-1.6 3.4 1v2a1.5 1.5 0 0 1-1.6 1.5A12.5 12.5 0 0 1 4.7 5.1a1.5 1.5 0 0 1 1.5-1.6z"/>',
  task: '<rect x="4" y="3.5" width="12" height="13.5" rx="1.5"/><path d="m7 10 2 2 4-4"/>',
  user: '<circle cx="10" cy="7" r="3"/><path d="M4 16.5c.9-2.8 3.3-4.5 6-4.5s5.1 1.7 6 4.5"/>',
  users:
    '<circle cx="7.5" cy="7.5" r="2.5"/><path d="M2.5 16c.7-2.4 2.6-3.8 5-3.8s4.3 1.4 5 3.8"/><path d="M12.5 5.2a2.5 2.5 0 0 1 0 4.6M14.5 12.4c1.4.5 2.4 1.8 3 3.6"/>',
  building: '<path d="M4 17V4h8v13M12 8h4v9M2.5 17h15M7 7h2M7 10h2M7 13h2"/>',
  dashboard:
    '<rect x="3.5" y="3.5" width="5.5" height="5.5" rx="1"/><rect x="11" y="3.5" width="5.5" height="5.5" rx="1"/><rect x="3.5" y="11" width="5.5" height="5.5" rx="1"/><rect x="11" y="11" width="5.5" height="5.5" rx="1"/>',
  list: '<path d="M7.5 5.5h9M7.5 10h9M7.5 14.5h9M3.5 5.5h.5M3.5 10h.5M3.5 14.5h.5"/>',
  inbox: '<path d="M3 11.5 5 5h10l2 6.5V15a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/><path d="M3 11.5h4l1 2h4l1-2h4"/>',
  chart: '<path d="M3 16.5h14M5.5 13.5v-4M10 13.5v-9M14.5 13.5v-6"/>',
  shield: '<path d="M10 3 16 5.5V10c0 3.4-2.4 6-6 7-3.6-1-6-3.6-6-7V5.5z"/><path d="m7.5 10 1.8 1.8L12.8 8.3"/>',
  settings:
    '<path d="M3.5 6h7.5M15 6h1.5M3.5 14h1.5M9 14h7.5"/><circle cx="13" cy="6" r="2"/><circle cx="7" cy="14" r="2"/>',
  search: '<circle cx="9" cy="9" r="5"/><path d="m13 13 4 4"/>',
  filter: '<path d="M3.5 5h13l-5 6v5l-3-1.5V11z"/>',
  plus: '<path d="M10 4v12M4 10h12"/>',
  more: '<circle fill="currentColor" cx="5" cy="10" r=".75"/><circle fill="currentColor" cx="10" cy="10" r=".75"/><circle fill="currentColor" cx="15" cy="10" r=".75"/>',
  edit: '<path d="m13.2 3.8 3 3L7 16H4v-3z"/><path d="m11.5 5.5 3 3"/>',
  eye: '<path d="M2.5 10S5.2 4.8 10 4.8 17.5 10 17.5 10 14.8 15.2 10 15.2 2.5 10 2.5 10z"/><circle cx="10" cy="10" r="2.3"/>',
  'chevron-down': '<path d="m6 8 4 4 4-4"/>',
  'chevron-right': '<path d="m8 6 4 4-4 4"/>',
  'arrow-up': '<path d="M10 15.5v-11M5.5 9 10 4.5 14.5 9"/>',
  'arrow-down': '<path d="M10 4.5v11M5.5 11l4.5 4.5 4.5-4.5"/>',
  // R6f (approved 28.10.): the 24 meanings the app had no icon for (docs/design/ICON-ZUORDNUNG.md).
  send: '<path d="M3.5 6.5h5M3.5 13.5h5"/><path d="M7 10h9.5M13 6.5l3.5 3.5-3.5 3.5"/>',
  reply: '<path d="M7.5 5 3.5 9l4 4"/><path d="M3.5 9h8a5 5 0 0 1 5 5v1"/>',
  connected:
    '<circle cx="8" cy="7" r="3"/><path d="M2.5 16.5c.9-2.8 3.1-4.5 5.5-4.5.9 0 1.8.2 2.6.6"/><path d="m12 14.5 1.75 1.75L17 12.75"/>',
  skip: '<path d="M3 14.5C3.5 8 5.5 5 8.5 5c3.4 0 5.8 2.8 7 7"/><path d="m16.84 9.59-1.34 2.41-2.41-1.34"/><path d="M8.5 9v6"/>',
  delete:
    '<path d="M3.5 5.5h13M8 5.5V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5"/><path d="m5 5.5.9 10.6a1 1 0 0 0 1 .9h6.2a1 1 0 0 0 1-.9L15 5.5"/><path d="M8.5 8.5v5M11.5 8.5v5"/>',
  archive:
    '<rect x="3" y="4" width="14" height="3.5" rx="1"/><path d="M4.5 7.5V15a1.5 1.5 0 0 0 1.5 1.5h8a1.5 1.5 0 0 0 1.5-1.5V7.5M8.5 10.5h3"/>',
  unarchive:
    '<rect x="3" y="4" width="14" height="3.5" rx="1"/><path d="M4.5 7.5V15a1.5 1.5 0 0 0 1.5 1.5h8a1.5 1.5 0 0 0 1.5-1.5V7.5"/><path d="M10 14v-4M8 12l2-2 2 2"/>',
  restore:
    '<path d="M3.5 5.5h13M8 5.5V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5"/><path d="m5 5.5.9 10.6a1 1 0 0 0 1 .9h6.2a1 1 0 0 0 1-.9L15 5.5"/><path d="M10 14V9M8 11l2-2 2 2"/>',
  duplicate:
    '<rect x="7.5" y="7.5" width="9" height="9" rx="1.5"/><path d="M12.5 7.5V5a1.5 1.5 0 0 0-1.5-1.5H5A1.5 1.5 0 0 0 3.5 5v6A1.5 1.5 0 0 0 5 12.5h2.5"/>',
  compare:
    '<path d="M3.5 3.5v13M16.5 3.5v13"/><path d="M6.5 7h7M11 4.5 13.5 7 11 9.5"/><path d="M13.5 13h-7M9 10.5 6.5 13 9 15.5"/>',
  upload: '<path d="M3.5 12.5v2a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-2"/><path d="M10 13V3.5M6.5 7 10 3.5 13.5 7"/>',
  download: '<path d="M3.5 12.5v2a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-2"/><path d="M10 3.5V13M6.5 9.5 10 13l3.5-3.5"/>',
  import:
    '<path d="M8 6V5a1.5 1.5 0 0 1 1.5-1.5h5.5A1.5 1.5 0 0 1 16.5 5v10a1.5 1.5 0 0 1-1.5 1.5H9.5A1.5 1.5 0 0 1 8 15v-1"/><path d="M3 10h10M10.5 7.5 13 10l-2.5 2.5"/>',
  sync: '<path d="M4 10a6 6 0 0 1 10.24-4.24"/><path d="M11.74 5.76h2.5v-2.5"/><path d="M16 10a6 6 0 0 1-10.24 4.24"/><path d="M8.26 14.24h-2.5v2.5"/>',
  disconnect:
    '<path d="m9.12 6.64 2.12-2.12a3 3 0 0 1 4.24 4.24l-2.12 2.12"/><path d="m10.88 13.36-2.12 2.12a3 3 0 0 1-4.24-4.24l2.12-2.12"/><path d="M6.5 6.5 5.25 5.25M13.5 13.5l1.25 1.25"/>',
  'sign-out': '<path d="M8 3.5H5A1.5 1.5 0 0 0 3.5 5v10A1.5 1.5 0 0 0 5 16.5h3"/><path d="M8.5 10h8M13.5 7l3 3-3 3"/>',
  'chevron-left': '<path d="m12 6-4 4 4 4"/>',
  lock: '<rect x="4.5" y="9" width="11" height="8" rx="1.5"/><path d="M7 9V6.5a3 3 0 0 1 6 0V9"/><path d="M10 12.25v1.5"/>',
  suppression: '<circle cx="10" cy="10" r="7"/><path d="m5.05 5.05 9.9 9.9"/>',
  document:
    '<path d="M11.5 3H6a1.5 1.5 0 0 0-1.5 1.5v11A1.5 1.5 0 0 0 6 17h8a1.5 1.5 0 0 0 1.5-1.5V7z"/><path d="M11.5 3v4h4M7.5 10.5h5M7.5 13.5h5"/>',
  website: '<circle cx="10" cy="10" r="7"/><ellipse cx="10" cy="10" rx="3" ry="7"/><path d="M3 10h14"/>',
  cloud: '<path d="M6.5 15.5a3.5 3.5 0 0 1-.39-6.98 4.5 4.5 0 0 1 8.89 1.15A3 3 0 0 1 14 15.5z"/>',
  folder:
    '<path d="M3 6a1.5 1.5 0 0 1 1.5-1.5h3.4l1.6 2h6A1.5 1.5 0 0 1 17 8v6.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 14.5z"/>',
  'cross-channel':
    '<path d="M3.5 10h3c2 0 3.5-4.5 7-4.5h3"/><path d="M6.5 10c2 0 3.5 4.5 7 4.5h3"/><path d="M14 3l2.5 2.5L14 8M14 12l2.5 2.5L14 17"/>',
} as const;

export type IconName = keyof typeof ICONS;

export interface IconProps {
  name: IconName;
  /** In px; 16 in controls, rows and nav, 20 standalone, 12 in badges. */
  size?: number;
  /** Set it when the icon is the only thing that says what it means; otherwise it is hidden from screen readers. */
  label?: string;
  className?: string;
  style?: string;
}

/** An icon, or nothing for a name the system does not have. */
export function icon({ name, size, label, className, style }: IconProps): TemplateResult | typeof nothing {
  const body = (ICONS as Record<string, string>)[name];
  if (!body) return nothing;
  const px = size || 16;
  return html`<svg
    class=${cx('sdr-icon', className)}
    width=${px}
    height=${px}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    role=${ifDefined(label ? 'img' : undefined)}
    aria-label=${ifDefined(label ?? undefined)}
    aria-hidden=${ifDefined(label ? undefined : 'true')}
    focusable="false"
    style=${ifDefined(style)}
  >${unsafeSVG(body)}</svg>`;
}
